import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Minus, Sparkles, ArrowRight } from 'lucide-react';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer 
} from 'recharts';
import { WA_NUMBER } from '../components/FloatingWhatsApp';
import { saveLead } from '../lib/leads';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PrescriptionUnlock from '../components/PrescriptionUnlock';
import WellnessChampion from '../components/WellnessChampion';
import RecommendedExperts from '../components/RecommendedExperts';
import { getReturnTo, clearReturnTo } from '../lib/personalization';
import { useTranslation } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const dimensionColors = {
  physical: '#38BDF8',
  mental: '#818CF8',
  emotional: '#C084FC',
  financial: '#34D399',
  career: '#FBBF24',
  lifestyle: '#F472B6',
  spiritual: '#A78BFA',
  self_care: '#F59E0B'
};

// Pretty labels for snake_case dimension keys
const dimensionLabels = {
  physical: 'Physical',
  mental: 'Mental',
  emotional: 'Emotional',
  financial: 'Financial',
  career: 'Career',
  lifestyle: 'Lifestyle & Environmental',
  spiritual: 'Spiritual',
  self_care: 'Self-Care'
};

const insights = {
  physical: {
    low: 'You have low energy and inconsistent physical habits',
    medium: 'Your physical health is average but needs improvement',
    high: 'You maintain good physical health and energy'
  },
  mental: {
    low: 'Mental fatigue and focus issues are affecting you',
    medium: 'Your mental clarity varies throughout the day',
    high: 'You have strong mental clarity and focus'
  },
  emotional: {
    low: 'Emotional regulation needs attention and support',
    medium: 'Your emotional state is manageable but inconsistent',
    high: 'You have healthy emotional awareness and balance'
  },
  financial: {
    low: 'Financial stress is significantly impacting your life',
    medium: 'Your finances are stable but could be optimized',
    high: 'You have strong financial stability and planning'
  },
  career: {
    low: 'Career dissatisfaction is affecting overall well-being',
    medium: 'Your career is acceptable but lacks fulfillment',
    high: 'You feel satisfied and engaged in your career'
  },
  lifestyle: {
    low: 'Your lifestyle lacks structure and balance',
    medium: 'You have some routines but need more consistency',
    high: 'You maintain a balanced and intentional lifestyle'
  },
  spiritual: {
    low: 'You feel disconnected from purpose and meaning',
    medium: 'You are exploring purpose but need more clarity',
    high: 'You have a strong sense of purpose and connection'
  },
  self_care: {
    low: 'Self-care and grooming are not a priority right now',
    medium: 'You invest in self-care occasionally — there is room to deepen the ritual',
    high: 'You consistently invest in skincare, grooming and overall self-care'
  }
};

const SCORE_LABELS = [
  { min: 80, label: 'Optimized', color: '#34D399' },
  { min: 60, label: 'Functional', color: '#FBBF24' },
  { min: 40, label: 'Needs Attention', color: '#F59E0B' },
  { min: 0, label: 'Intervention Needed', color: '#EF4444' },
];

function getScoreLabel(score) {
  return SCORE_LABELS.find((s) => score >= s.min) || SCORE_LABELS[SCORE_LABELS.length - 1];
}

function getTrendIcon(score) {
  if (score >= 70) return <TrendingUp className="w-5 h-5" />;
  if (score >= 40) return <Minus className="w-5 h-5" />;
  return <TrendingDown className="w-5 h-5" />;
}

function getInsight(dimension, score) {
  const level = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';
  return insights[dimension]?.[level] || '';
}

function Result() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch (error) {
      // User not authenticated, that's okay
      setCurrentUser(null);
    }
  }, []);

  const fetchAssessment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/assessments/${assessmentId}`);
      setAssessment(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      toast.error('Failed to load results');
      navigate('/');
    }
  }, [assessmentId, navigate]);

  const [previous, setPrevious] = useState(null); // { total_score, dimension_scores, assessment_id, saved_at }

  useEffect(() => {
    fetchAssessment();
    checkAuth();
  }, [fetchAssessment, checkAuth]);

  // ---- Retake comparison: compare with the previous assessment stored in localStorage ----
  useEffect(() => {
    if (!assessment) return;
    try {
      const raw = localStorage.getItem('clinity_last_assessment');
      const prev = raw ? JSON.parse(raw) : null;
      // Show comparison only when we have a previous *different* assessment
      if (prev && prev.assessment_id && prev.assessment_id !== assessmentId && typeof prev.total_score === 'number') {
        setPrevious(prev);
      }
      // Then persist the current assessment as the new "last" for next time
      const snapshot = {
        assessment_id: assessmentId,
        total_score: assessment.total_score,
        dimension_scores: assessment.dimension_scores || {},
        saved_at: new Date().toISOString(),
      };
      localStorage.setItem('clinity_last_assessment', JSON.stringify(snapshot));
    } catch (_) { /* localStorage unavailable — no-op */ }
  }, [assessment, assessmentId]);

  // ---- WhatsApp share URL (built eagerly so the <a href> is ready before any tap) ----
  // Using a real anchor element (instead of window.open in a click handler) is the
  // pattern that "previously worked" — mobile browsers (Safari, Samsung Internet,
  // Chrome on Android) treat the navigation as the user gesture itself, so popup
  // blockers / Smart Anti-Tracking never interfere.
  const waUrl = useMemo(() => {
    if (!assessment) return '';
    const score = assessment.total_score;
    const d = assessment.dimension_scores || {};
    const name = currentUser?.name || '';
    const lines = [
      t('result.share.greeting'),
      '',
      name ? `${t('result.share.name')}: ${name}` : null,
      `${t('result.share.score')}: ${score}/100`,
      '',
      t('result.share.detailed'),
      `- ${t('dim.physical')}: ${d.physical ?? '-'}`,
      `- ${t('dim.mental')}: ${d.mental ?? '-'}`,
      `- ${t('dim.emotional')}: ${d.emotional ?? '-'}`,
      `- ${t('dim.financial')}: ${d.financial ?? '-'}`,
      `- ${t('dim.career')}: ${d.career ?? '-'}`,
      `- ${t('dim.lifestyle')}: ${d.lifestyle ?? '-'}`,
      `- ${t('dim.spiritual')}: ${d.spiritual ?? '-'}`,
      `- ${t('dim.self_care')}: ${d.self_care ?? '-'}`,
      '',
      t('result.share.footer'),
    ].filter((l) => l !== null);
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
  }, [assessment, currentUser, t]);

  // Fire-and-forget lead capture on click. We do NOT await it — `keepalive: true`
  // inside saveLead lets the request survive the navigation to wa.me.
  const recordWhatsAppLead = useCallback(() => {
    if (!assessment) return;
    const score = assessment.total_score;
    const d = assessment.dimension_scores || {};
    const name = currentUser?.name || '';
    saveLead({
      type: 'assessment',
      name,
      phone: currentUser?.phone || '',
      email: currentUser?.email || '',
      assessment_id: assessmentId,
      totalScore: score,
      physical: d.physical ?? null,
      mental: d.mental ?? null,
      emotional: d.emotional ?? null,
      financial: d.financial ?? null,
      career: d.career ?? null,
      lifestyle: d.lifestyle ?? null,
      spiritual: d.spiritual ?? null,
      self_care: d.self_care ?? null,
    });
  }, [assessment, assessmentId, currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="result-loading">
        <div className="spinner w-8 h-8 border-4 border-[#4F46E5] border-t-transparent rounded-full" />
      </div>
    );
  }

  const scoreLabel = getScoreLabel(assessment.total_score);
  
  // Prepare chart data
  const chartData = Object.entries(assessment.dimension_scores).map(([key, value]) => ({
    dimension: t(`dim.${key}`),
    score: value,
    fullMark: 100
  }));

  return (
    <div className="min-h-screen relative overflow-hidden py-12 px-6" style={{ backgroundColor: '#F8FAFC' }} data-testid="result-page">
      {/* Background pattern */}
      <div className="absolute inset-0 pattern-dots z-0" />
      
      {/* Decorative gradients */}
      <div 
        className="absolute top-10 -right-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)' }}
      />
      <div 
        className="absolute bottom-10 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 70%)' }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Language switcher (top-right) */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>

        {/* Continue banner — shown when user came from /experts or /spaces */}
        <ReturnToBanner />

        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <div className="flex items-center justify-center gap-2 mb-4" data-testid="result-brand">
            <span className="text-base font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>
              Clinity
            </span>
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 text-[#0F172A]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="result-title"
          >
            {t('result.title.part1')}<span className="gradient-text">{t('result.title.part2')}</span>
          </h1>
        </div>

        {/* Retake comparison — shown only if user has a previous different assessment */}
        {previous && (
          <RetakeComparison current={assessment} previous={previous} t={t} />
        )}

        {/* Wellness Wheel */}
        <div
          id="result-card"
          className="bg-white border border-[#E2E8F0] rounded-3xl p-8 mb-8 fade-in shadow-soft-lg"
          data-testid="wellness-wheel"
        >
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold mb-2 text-[#0F172A]">Your Wellness Score</h2>
            <div className="text-6xl font-bold mb-2" style={{ color: scoreLabel.color }} data-testid="total-score">
              {assessment.total_score}
            </div>
            <p className="text-lg mb-4" style={{ color: scoreLabel.color }}>
              {scoreLabel.label}
            </p>
          </div>

          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={chartData}>
              <PolarGrid stroke="rgba(99, 102, 241, 0.15)" />
              <PolarAngleAxis 
                dataKey="dimension" 
                tick={{ fill: '#475569', fontSize: 13, fontWeight: 500 }}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fill: '#94A3B8', fontSize: 11 }}
              />
              <Radar
                name="Score"
                dataKey="score"
                stroke="#4F46E5"
                fill="rgba(79, 70, 229, 0.2)"
                strokeWidth={2.5}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Dimension Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {Object.entries(assessment.dimension_scores).map(([dimension, score]) => (
            <div
              key={dimension}
              className="bg-white border border-[#E2E8F0] rounded-2xl p-6 hover-scale shadow-soft"
              data-testid={`dimension-${dimension}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-[#0F172A]">{t(`dim.${dimension}`)}</h3>
                <div 
                  className="flex items-center gap-2"
                  style={{ color: dimensionColors[dimension] }}
                >
                  {getTrendIcon(score)}
                  <span className="text-2xl font-bold">{score}</span>
                </div>
              </div>
              <p className="text-sm text-[#64748B]">{getInsight(dimension, score)}</p>
            </div>
          ))}
        </div>

        {/* Wellness Champion — celebration (≥70) or progress card (<70) */}
        <WellnessChampion score={assessment.total_score} dimensionScores={assessment.dimension_scores} />

        {/* Recommended Experts — dimension-driven expert categories */}
        <RecommendedExperts assessmentId={assessmentId} />

        {/* Premium Free Prescription unlock + Personalization form + Ready state */}
        <div className="mb-8">
          <PrescriptionUnlock assessmentId={assessmentId} />
        </div>

        {/* Retake Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/assessment')}
            className="text-[#64748B] hover:text-[#4F46E5] text-sm transition-colors font-medium"
            data-testid="retake-btn"
          >
            {t('result.cta.retake')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Result;

/* ---------------- Continue-to Banner ---------------- */
function ReturnToBanner() {
  const [target, setTarget] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setTarget(getReturnTo());
  }, []);

  if (!target) return null;

  const targetLabel = target === '/experts'
    ? 'Wellness Experts'
    : target === '/spaces'
      ? 'Wellness Spaces & Retreats'
      : 'Improve Your Score';

  const handleContinue = () => {
    clearReturnTo();
    navigate(target);
  };

  return (
    <div
      className="mb-6 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
      style={{
        background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
        border: '1px solid #E0E7FF',
        boxShadow: '0 8px 20px -12px rgba(79,70,229,0.2)',
      }}
      data-testid="return-to-banner"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#4F46E5' }} />
        <div>
          <div className="text-[13px] font-bold" style={{ color: '#0F172A', fontFamily: 'Outfit, sans-serif' }}>
            Your score is ready
          </div>
          <div className="text-[13px]" style={{ color: '#475569' }}>
            Continue to <span className="font-semibold" style={{ color: '#4F46E5' }}>{targetLabel}</span> to get personalised recommendations.
          </div>
        </div>
      </div>
      <button
        onClick={handleContinue}
        data-testid="return-to-continue-btn"
        className="group shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[13px] text-white transition-all duration-300 hover:-translate-y-0.5"
        style={{
          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 60%, #A855F7 100%)',
          boxShadow: '0 12px 24px -10px rgba(139,92,246,0.55)',
        }}
      >
        Continue
        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
      </button>
    </div>
  );
}

/* ---------------- Retake Comparison Card ---------------- */
function RetakeComparison({ current, previous, t }) {
  const delta = current.total_score - previous.total_score;
  const isUp = delta > 0;
  const isDown = delta < 0;
  const badgeColor = isUp ? '#059669' : isDown ? '#DC2626' : '#475569';
  const badgeBg    = isUp ? '#D1FAE5' : isDown ? '#FEE2E2' : '#F1F5F9';
  const sign = isUp ? '+' : '';

  const prevSavedNice = (() => {
    try { return new Date(previous.saved_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); }
    catch { return ''; }
  })();

  // Top 2 dimensions with biggest movement
  const dimMoves = Object.entries(current.dimension_scores || {})
    .map(([k, v]) => ({ key: k, delta: v - (previous.dimension_scores?.[k] ?? v) }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 3);

  return (
    <div
      className="mb-6 rounded-3xl p-5 sm:p-6 border border-indigo-100"
      style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)' }}
      data-testid="retake-comparison"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#4F46E5] mb-1.5 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Progress since last time
          </div>
          <p className="text-[14px] sm:text-[15px] text-[#334155] leading-[1.55]">
            You scored <span className="font-bold text-[#0F172A]">{previous.total_score}</span>
            {prevSavedNice ? ` on ${prevSavedNice}` : ''} · now <span className="font-bold text-[#0F172A]">{current.total_score}</span>.
          </p>
        </div>
        <span
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[13px] font-bold tabular-nums"
          style={{ color: badgeColor, background: badgeBg, border: `1px solid ${badgeColor}22` }}
          data-testid="retake-delta"
        >
          {isUp && <TrendingUp className="w-3.5 h-3.5" />}
          {isDown && <TrendingDown className="w-3.5 h-3.5" />}
          {!isUp && !isDown && <Minus className="w-3.5 h-3.5" />}
          {sign}{delta} pts
        </span>
      </div>

      {dimMoves.some((d) => d.delta !== 0) && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="retake-dim-moves">
          {dimMoves.map((d) => {
            const up = d.delta > 0, down = d.delta < 0;
            const c = up ? '#059669' : down ? '#DC2626' : '#475569';
            return (
              <span
                key={d.key}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold bg-white/70 border border-white"
                style={{ color: c }}
              >
                {t(`dim.${d.key}`)} {d.delta > 0 ? '+' : ''}{d.delta}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
